/**
 * SEO Helper Utilities
 * Common functions for generating SEO-friendly content
 */

/**
 * Truncates text to a specified length with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Generates a meta description from text
 * Recommended length: 150-160 characters
 */
export const generateMetaDescription = (text: string, maxLength: number = 160): string => {
  const cleaned = text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  return truncateText(cleaned, maxLength);
};

/**
 * Generates a canonical URL
 */
export const generateCanonicalUrl = (path: string): string => {
  const baseUrl = 'https://trainedup.ai';
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Extracts keywords from text
 */
export const extractKeywords = (text: string, maxKeywords: number = 10): string[] => {
  const commonWords = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
    'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
    'to', 'was', 'will', 'with', 'you', 'your', 'this', 'can', 'will',
  ]);

  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter(word => word.length > 3 && !commonWords.has(word));

  const wordCount = new Map<string, number>();
  words.forEach(word => {
    wordCount.set(word, (wordCount.get(word) || 0) + 1);
  });

  return Array.from(wordCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxKeywords)
    .map(([word]) => word);
};

/**
 * Sanitizes text for SEO (removes special characters, normalizes spaces)
 */
export const sanitizeForSEO = (text: string): string => {
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/\s+/g, ' ')     // Normalize whitespace
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
    .trim();
};

/**
 * Converts a title to URL-friendly slug
 */
export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

/**
 * Generates Open Graph image URL (can be extended to generate dynamic OG images)
 */
export const generateOGImageUrl = (_title?: string): string => {
  // For now, return default. In the future, this could generate dynamic OG images
  // using a service like Vercel OG Image Generation
  return 'https://trainedup.ai/tulogo.png';
};

/**
 * Estimates reading time based on word count
 */
export const estimateReadingTime = (text: string): string => {
  const wordsPerMinute = 200;
  const wordCount = text.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

/**
 * Formats ISO duration for schema.org (e.g., "PT2H30M" for 2 hours 30 minutes)
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `PT${mins}M`;
  } else if (mins === 0) {
    return `PT${hours}H`;
  } else {
    return `PT${hours}H${mins}M`;
  }
};
